import React, { useState, useCallback } from 'react';
import { Element, Transforms, Location } from 'slate';
import {
  ReactEditor,
  useSelected,
  useReadOnly,
  useSlateStatic,
} from 'slate-react';
import copy from 'copy-to-clipboard';
import { IconButton, icons } from '@ublend-npm/aulaui-next';
import {
  FailedDescription,
  Failed,
  FailedIcon,
  BlockButton,
} from '../File/File.styled';
import {
  CloudFileContainer,
  CloudFileDetails,
  CloudFileEmbedContainer,
  CloudFileName,
} from './CloudFile.styled';
import { BlockToolbar } from '../BlockToolbar/BlockToolbar';
import { EmbeddedIframe } from '../EmbeddedIframe/EmbeddedIframe';
import { removeVoidElement } from '../../lib/commands';
import { AulaEditorInstance } from '../../types';

export type CloudFileProps = {
  element: Element;
  icon: JSX.Element;
  cloudFileType: string;
  requestPicker: (editor: AulaEditorInstance, location: Location) => void;
};

export type CloudFileElement = Element & {
  failed?: boolean;
  title: string;
  viewUrl: string;
  downloadUrl?: string;
  embedUrl?: string;
  source: string;
};

export const CloudFile = (props: CloudFileProps) => {
  const isSelected = useSelected();
  const isReadOnly = useReadOnly();
  const editor = useSlateStatic();
  const [isHovering, setHovering] = useState(false);
  const [isEmbedExpanded, setIsEmbedExpanded] = useState(true);
  const { element, icon, requestPicker, cloudFileType } = props;
  const { failed, title, viewUrl, downloadUrl, embedUrl } =
    element as CloudFileElement;

  // When the user presses the Retry button, we remove the failed state element
  // and start the entire process again:
  const onRetry = useCallback(() => {
    const at = ReactEditor.findPath(editor, element);
    Transforms.removeNodes(editor, { at });
    requestPicker(editor, at);
  }, [editor, element, requestPicker]);

  const onToggleEmbedExpand = useCallback(
    (event: Event) => {
      event.preventDefault();
      setIsEmbedExpanded(!isEmbedExpanded);
    },
    [isEmbedExpanded],
  );

  const hoverStateProps = {
    onMouseEnter: () => setHovering(true),
    onMouseLeave: () => setHovering(false),
  };

  const toolbarItems = [
    {
      title: 'Delete',
      isVisible: !isReadOnly,
      icon: icons.Delete,
      onAction: () => removeVoidElement(editor, element),
    },
    {
      title: 'Copy link',
      icon: icons.CopyLink,
      isVisible: !failed,
      onAction: () => copy(viewUrl),
    },
    {
      title: 'Download',
      icon: icons.CloudDownloadIcon,
      isVisible: !!downloadUrl && !failed,
      onAction: () => window.open(downloadUrl),
    },
    {
      title: 'Open in new tab',
      icon: icons.OpenInNew,
      isVisible: !failed,
      onAction: () => window.open(viewUrl),
    },
  ];

  const hoverToolbar = (
    <BlockToolbar isVisible={isSelected || isHovering} items={toolbarItems} />
  );

  if (failed) {
    return (
      <Failed
        isSelected={isSelected}
        {...hoverStateProps}
        contentEditable={false}
        data-testid="failed-upload"
      >
        <FailedIcon>
          <icons.ErrorIcon />
        </FailedIcon>
        <FailedDescription>
          Failed to insert file due to an unknown error.
          {!isReadOnly && (
            <>
              <br />
              <BlockButton onClick={onRetry}>Retry upload</BlockButton>
            </>
          )}
        </FailedDescription>
        {hoverToolbar}
      </Failed>
    );
  }

  return (
    <CloudFileContainer
      isSelected={isSelected}
      {...hoverStateProps}
      rel="noreferrer"
      data-testid={`file-${cloudFileType}`}
      href={viewUrl}
      target="_blank"
    >
      <CloudFileDetails>
        {icon}
        <CloudFileName>{title}</CloudFileName>
        {embedUrl ? (
          <IconButton
            icon={isEmbedExpanded ? icons.ArrowDropUp : icons.ArrowDropDown}
            label="collapsible-embedded-button"
            type="secondary"
            size="small"
            onClick={onToggleEmbedExpand}
          />
        ) : null}
      </CloudFileDetails>
      {isEmbedExpanded && embedUrl ? (
        <CloudFileEmbedContainer>
          <EmbeddedIframe
            title={title}
            src={embedUrl}
            minHeight={400}
            maxHeight={undefined}
            width="100%"
            maxWidth={undefined}
            minWidth={undefined}
            oEmbedApi={undefined}
            oEmbedUrl={undefined}
          />
        </CloudFileEmbedContainer>
      ) : null}
      {hoverToolbar}
    </CloudFileContainer>
  );
};
